'use client'
import React, { useState, useMemo, useEffect } from 'react'
import { Box, Switch } from '@mui/material'
import { QuestionHelper, CustomModal, NumericalInput } from '@/components'
import { useSwapActionHandlers } from '@/state/swap/hooks'
import { useUserSlippageTolerance, useSlippageManuallySet, useUserTransactionTTL } from '@/state/user/hooks'
import { SLIPPAGE_AUTO } from '@/constants'
import { Cancel01Icon, Alert01Icon } from '@/utils/icons'

enum SlippageError {
  InvalidInput = 'InvalidInput',
  RiskyLow = 'RiskyLow',
  RiskyHigh = 'RiskyHigh',
}

enum DeadlineError {
  InvalidInput = 'InvalidInput',
}

interface SettingsModalProps {
  open: boolean
  onClose: () => void
  defaultSlippage?: number
}
const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose, defaultSlippage = 0 }) => {
  const [enableCustom, setEnableCustom] = useState<boolean>(false)
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippageTolerance()
  const [slippageManuallySet, setSlippageManuallySet] = useSlippageManuallySet()
  
  const [ttl, setTtl] = useUserTransactionTTL()
  const { onRecipientChange } = useSwapActionHandlers()
  const [slippageInput, setSlippageInput] = useState('')
  const [deadlineInput, setDeadlineInput] = useState('')
  const userSlippageIsSet = !!userSlippageTolerance
  useEffect(() => {
    if (!userSlippageIsSet && defaultSlippage > 0) {
      setUserSlippageTolerance(defaultSlippage)
    }
  }, [defaultSlippage, setUserSlippageTolerance, userSlippageIsSet])

  const slippageInputIsValid =
  slippageInput === '' ||
  (userSlippageTolerance / 100).toFixed(2) ===
    Number.parseFloat(slippageInput).toFixed(2)
  const deadlineInputIsValid =
  deadlineInput === '' || (ttl / 60).toString() === deadlineInput

  const slippageError = useMemo(() => {
    if (slippageInput !== '' && !slippageInputIsValid) {
      return SlippageError.InvalidInput
    } else if (userSlippageTolerance === SLIPPAGE_AUTO) {
      return undefined
    } else if (slippageInputIsValid && userSlippageTolerance < 50) {
      return SlippageError.RiskyLow
    } else if (slippageInputIsValid && userSlippageTolerance > 500) {
      return SlippageError.RiskyHigh
    } else {
      return undefined
    }
  }, [slippageInput, userSlippageTolerance, slippageInputIsValid])
  const slippageAlert =
    !!slippageInput && // eslint-disable-line
    (slippageError === SlippageError.RiskyLow ||
    slippageError === SlippageError.RiskyHigh)

  const deadlineError = useMemo(() => { // eslint-disable-line
    if (deadlineInput !== '' && !deadlineInputIsValid) {
      return DeadlineError.InvalidInput
    } else {
      return undefined
    }
  }, [deadlineInput, deadlineInputIsValid])
  const parseCustomSlippage = (value: string) => {
    setSlippageInput(value)

    try {
      const valueAsIntFromRoundedFloat = Number.parseInt(
        (Number.parseFloat(value) * 100).toString()
      )
      if (
        !Number.isNaN(valueAsIntFromRoundedFloat) &&
        valueAsIntFromRoundedFloat < 5000
      ) {
        setUserSlippageTolerance(valueAsIntFromRoundedFloat)
        if (userSlippageTolerance !== valueAsIntFromRoundedFloat) {
          setSlippageManuallySet(true)
        }
      }
    } catch {}
  }
  const parseCustomDeadline = (value: string) => {
    setDeadlineInput(value)

    try {
      const valueAsInt: number = Number.parseInt(value) * 60
      if (!Number.isNaN(valueAsInt) && valueAsInt > 0) {
        setTtl(valueAsInt)
      }
    } catch {}
  }

  /**
   *

   */
  return (
    <CustomModal open={open} onClose={onClose} classname='p-6 max-w-[470px]'>
      <Box mb={3} className='flex items-center justify-between'>
        <h5 className='font-semibold text-xl'>Settings</h5>
        <Cancel01Icon onClick={onClose} width={24} className='text-white' />
      </Box>
      <Box my={1} className='flex items-center'>
        <Box mr='6px'>
          <p className='text-textSecondary'>Slippage tolerance</p>
        </Box>
        <QuestionHelper size={20} text='Your transaction will revert if the price changes unfavorably by more than this percentage' />
      </Box>
      <Box className='border rounded-xl border-primary border-opacity-60 bg-primary/20 shadow-xl shadow-primary/10'>
        <Box className='flex items-center p-2 gap-2 font-regular justify-around'>
          <Box
            className={`w-[62px] h-10 rounded-md bg-primary/10 flex items-center justify-center cursor-pointer ${
                userSlippageTolerance === SLIPPAGE_AUTO
                  ? 'bg-primary/60'
                  : ''
              }`}
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(SLIPPAGE_AUTO)
              if (userSlippageTolerance !== SLIPPAGE_AUTO) {
                setSlippageManuallySet(true)
              }
            }}
          >
            <small className='drop-shadow-glow'>AUTO</small>
          </Box>
          <Box
            className={`w-[62px] h-10 rounded-md  flex items-center justify-center cursor-pointer ${
                userSlippageTolerance === 10 ? ' bg-primary/60' : ''
              }`}
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(10)
              if (userSlippageTolerance !== 10) {
                setSlippageManuallySet(true)
              }
            }}
          >
            <small>0.1 %</small>
          </Box>
          <Box
            className={`w-[62px] h-10 rounded-md  flex items-center justify-center cursor-pointer ${
                userSlippageTolerance === 50 ? 'bg-primary/60' : ''
              }`}
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(50)
              if (userSlippageTolerance !== 50) {
                setSlippageManuallySet(true)
              }
            }}
          >
            <small>0.5 %</small>
          </Box>
          <Box
            className={`w-[62px] h-10 rounded-md flex items-center justify-center cursor-pointer  ${
                userSlippageTolerance === 100 ? ' bg-primary/60' : ''
              }`}
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(100)
              if (userSlippageTolerance !== 100) {
                setSlippageManuallySet(true)
              }
            }}
          >
            <small>1 %</small>
          </Box>
        </Box>
      </Box>
      <Box mt={1.4} className='px-3'>
        <div className='flex items-center justify-between w-full'>
          <small className='text-white'>Custom slippage</small>
          <Switch color='secondary' onClick={() => setEnableCustom(!enableCustom)} />
        </div>
      </Box>
      {enableCustom
        ? (
          <Box
            className={`w-full flex items-center ${
             slippageAlert ? 'text-yellow' : 'text-white'
           }`}
          >
            {slippageAlert && <Alert01Icon className='text-yellow mr-5' width={25} />}
            <div className='flex w-fit items-center justify-start border px-3 rounded-lg border-primary'>
              <NumericalInput
                className='text-md placeholder:text-md'
                placeholder={(userSlippageTolerance / 100).toFixed(2)}
                value={slippageInput}
                fontSize={14}
                fontWeight={500}
                align='left'
                onBlur={() => {
                  parseCustomSlippage((userSlippageTolerance / 100).toFixed(2))
                }}
                onUserInput={(value) => parseCustomSlippage(value)}
              />
              <small className='text-start'>%</small>
            </div>

          </Box>
          )
        : <></>}
      {slippageError && (
        <Box mt={1.5}>
          <small className='text-yellow font-regular text-sm'>
            {slippageError === SlippageError.InvalidInput
              ? 'Enter a valid slippage percentage'
              : slippageError === SlippageError.RiskyLow
                ? 'Your transaction may fail'
                : 'Your transaction may be frontrun'}
          </small>
        </Box>
      )}
      <Box my={2.5} className='flex items-center'>
        <Box mr='6px'>
          <p className='text-textSecondary'>Transaction Deadline</p>
        </Box>
        <QuestionHelper size={20} text='Your transaction will revert if it is pending for more than this long.' />
      </Box>
      <Box mb={2} className='flex items-center gap-3'>
        <Box className='flex h-10 rounded-md px-0 py-8 flex-1 items-center bg-bgColor' maxWidth={168}>
          <NumericalInput
            className='w-full relative outline-none whitespace-nowrap rounded-md text-ellipsis overflow-hidden text-left text-md placeholder:text-lg border border-primary border-opacity-40 px-3'
            placeholder={(ttl / 60).toString()}
            value={deadlineInput}
            fontSize={14}
            fontWeight={500}
            onBlur={() => {
              parseCustomDeadline((ttl / 60).toString())
            }}
            onUserInput={(value) => parseCustomDeadline(value)}
          />
        </Box>
        <Box ml={1}>
          <small>Minutes</small>
        </Box>
      </Box>
      {deadlineError && (
        <Box mt={1.5}>
          <small className='text-yellow'>Enter a valid deadline</small>
        </Box>
      )}
    </CustomModal>
  )
}
export default SettingsModal